import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useConversationStore } from '../stores/conversationStore';
import { useAuthStore } from '../stores/authStore';
import { ConversationSidebar } from './ConversationSidebar';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';

export const Chat: React.FC = () => {
  const [showSidebar, setShowSidebar] = useState(true);
  const location = useLocation();
  
  const { logout } = useAuthStore();
  const {
    conversations,
    currentConversation,
    messages,
    isLoading,
    isSending,
    error,
    loadConversations,
    createConversation,
    selectConversation,
    sendMessage,
    updateConversation,
    deleteConversation,
    clearError,
  } = useConversationStore();



  useEffect(() => {
    // Load conversations on mount
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    // Create a new conversation if none exists and we're not currently loading
    // Only create if we have definitely loaded conversations and there are none
    if (conversations.length === 0 && !currentConversation && !isLoading) {
      createConversation('New Conversation');
    }
  }, [conversations.length, currentConversation, isLoading, createConversation]);

  const handleSendMessage = async (message: string, files?: File[]) => {
    if (!currentConversation) {
      await createConversation('New Conversation');
    }
    await sendMessage(message, files);
  };

  const handleCreateConversation = async () => {
    await createConversation('New Conversation');
  };

  const handleSignOut = () => {
    logout();
  };



  return (
    <div className="flex h-screen bg-gray-50">
      {/* Error Toast */}
      {error && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg shadow-lg flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={clearError}
              className="ml-4 text-red-500 hover:text-red-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Sidebar Toggle for Mobile */}
      <button
        onClick={() => setShowSidebar(!showSidebar)}
        className="lg:hidden fixed top-4 left-4 z-40 p-2 bg-white rounded-lg shadow-lg"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {showSidebar ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Sidebar */}
      <div className={`${showSidebar ? 'block' : 'hidden'} lg:block`}>
        <ConversationSidebar
          conversations={conversations}
          currentConversationId={currentConversation?.id}
          onSelectConversation={selectConversation}
          onCreateConversation={handleCreateConversation}
          onUpdateConversation={updateConversation}
          onDeleteConversation={deleteConversation}
          onSignOut={handleSignOut}
        />
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-800">
              {currentConversation?.title || 'New Conversation'}
            </h1>
            <nav className="flex space-x-1">
              <Link
                to="/"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === '/' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                Chat
              </Link>
              <Link
                to="/files"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === '/files' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                Files
              </Link>
            </nav>
          </div>
        </div>

        {/* Messages Area */}
        <MessageList
          messages={messages}
          isLoading={isLoading}
          isSending={isSending}
        />

        {/* Input Area */}
        <div className="bg-white border-t">
          <ChatInput
            onSendMessage={handleSendMessage}
            disabled={isSending}
          />
        </div>
      </div>
    </div>
  );
};
